import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { Alert, Button, Field, Input, Spinner } from "../components/ui";
import { useAuth } from "../lib/auth";

/* Landing page for an admin's invite link: /gear/accept-invite?email=&token=
   The token in the URL is the credential, so all this asks for is a password. */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const email = params.get("email") || "";
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const linkLooksValid = Boolean(email && token);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await acceptInvite({ email, token, password });
      navigate("/gear");
    } catch (err) {
      setError(err.message || "Couldn't finish setting up your account.");
    } finally {
      setBusy(false);
    }
  }

  if (!linkLooksValid) {
    return (
      <AuthLayout title="That link looks incomplete">
        <Alert>
          This invite link is missing part of its address. Open the link from your email
          exactly as it was sent, or ask an SSA admin to send a new invite.
        </Alert>
        <p className="mt-4 text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/gear/login" className="font-semibold text-brand-600">Sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome to the Gear Exchange">
      <p className="text-sm text-slate-500">
        You've been invited as <span className="font-medium text-slate-700">{email}</span>.
        Choose a password to finish setting up your account.
      </p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label="Password" hint="At least 8 characters">
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </Field>
        <Field label="Confirm password">
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
          />
        </Field>
        <Alert>{error}</Alert>
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? <Spinner /> : "Create my account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
